import * as posActions from "../../lib/posActions";
import * as posSelectors from "../../lib/posSelectors";
import * as routeHelpers from "../../lib/routeHelpers";
import * as searchParamNames from "../../constants/searchParamNames";
import CheckoutItemOrCombo from "../CheckoutItemOrCombo";
import CheckoutProductBase from "../CheckoutProductBase";
import classNames from "classnames";
import ErrorModal from "../ErrorModal";
import logger from "../../lib/sentryLogger";
import OutlineButton from "../OutlineButton";
import PendingOrdersButton from "./PendingOrdersButton";
import React, {useEffect, useRef, useState} from "react";
import SelectableCheckoutComboOrItem from "../SelectableCheckoutComboOrItem";
import SplitModal from "../SplitModal";
import styles from "./CashierCheckoutItems.module.css";
import TextModal from "../TextModal";
import ToggleButton from "../ToggleButton";
import usePrevious from "../../lib/usePrevious";
import {useAppSelector, useAppDispatch} from "../../lib/hooks";
import {Actions, API, Lib, Constants} from "habit-core";
import {
    Routes,
    Route,
    useNavigate,
    Outlet,
    useSearchParams,
    useLocation,
} from "react-router-dom";

const strings = {
    toGo: "To-Go",
    dineIn: "Dine-In",
    proceed: "Proceed",
    total: "Total :",
    includesTax: "(Includes Tax)",
    editOrder: "Edit Order",
    noItems: "No Items in Order",
    giftCardValue: "Gift Card Value",
    giftCard: "Gift Card",
    proceedPayment: "Proceed To Payment",
    lane1: "Lane 1",
    lane2: "Lane 2",
    discountApplied: "Discount Applied:",
    startNewOrder: "Start A New Order",
    reserveOrderError:
        "An unexpected error occurred while reserving a new order id. Please try again",
    whoops: "Whoops!",
    activeOrder:
        "You are unable to view the kiosk cash orders until your current order is completed. Please complete or clear out this order to view the pending cash orders.",
    ok: "Ok",
};

type Props = {
    className?: string;
    orderData: Lib.selectors.CartEntity[];
};

const MAX_QUANTITY = 99;

export default function CashierCheckoutItems(props: Props) {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const location = useLocation();
    const [searchParams] = useSearchParams();
    // TODO: move to pos station state
    const isRequestingCCPayment =
        searchParams.get(searchParamNames.REQUESTING_CC_PAYMENT) === "true";
    const isTraining = useAppSelector((state) => state.pos.station.isTraining);
    const isSubmittingOrder = useAppSelector(
        (state) => state.pos.station.isSubmittingOrder,
    );
    const placeOrderAfterPaymentsProcessedErrored = useAppSelector(
        (state) => state.pos.errors.placeOrderAfterPaymentsProcessedErrored,
    );

    const assignedTill = useAppSelector(
        (state) => state.pos.station.assignedTill,
    );
    const trainingModeAssignedTill = useAppSelector(
        (state) => state.pos.station.trainingModeAssignedTill,
    );
    const tillAssignmentSkipped = useAppSelector(
        (state) => state.pos.station.tillAssignmentSkipped,
    );
    const isTillAssignmentRequired = isTraining
        ? trainingModeAssignedTill === -1
        : assignedTill === -1 && !tillAssignmentSkipped;

    const pendingOrderCount = useAppSelector(
        (state) => Object.keys(state.pos.pendingOrders.byId).length,
    );
    const posCurrentOrder = useAppSelector((state) => state.pos.currentOrder);
    const currentOrder = useAppSelector((state) => state.currentOrder);
    const orderEmpty =
        !currentOrder.comboCustomizationIds.length &&
        !currentOrder.itemCustomizationIds.length;

    const addFundsGiftCards = useAppSelector(
        (state) => state.pos.currentOrder.giftCards.addFunds,
    );
    const purchaseGiftCards = useAppSelector(
        (state) => state.pos.currentOrder.giftCards.purchase,
    );
    // TODO: #261 - support purchasing gift cards as part of regular order
    const isGiftCardsOrder =
        !!addFundsGiftCards.length || !!purchaseGiftCards.length;

    const stationMode = useAppSelector((state) => state.pos.station.mode);
    const laneAssignment = useAppSelector(
        (state) => state.pos.station.laneAssignment,
    );
    const driveThruDetails = useAppSelector(
        (state) => state.pos.currentOrder.driveThruDetails,
    );

    const [showActiveOrderModal, setShowActiveOrderModal] = useState(false);
    const isOrderCompleteScreen =
        location.pathname === routeHelpers.orderComplete();
    const preventViewingKioskOrders =
        (!isOrderCompleteScreen &&
            (stationMode === "front_of_house_1" ||
                stationMode === "front_of_house_2") &&
            !orderEmpty) ||
        isGiftCardsOrder;

    const voidedItemIds = useAppSelector(
        (state) => state.pos.currentOrder.voidedItemIds,
    );
    const voidedItemsById = useAppSelector(
        (state) => state.pos.currentOrderVoided.items.voided.byId,
    );
    const voidedComboIds = useAppSelector(
        (state) => state.pos.currentOrder.voidedComboIds,
    );
    const voidedCombosById = useAppSelector(
        (state) => state.pos.currentOrderVoided.combos.voided.byId,
    );
    const pendingVoidedItemsById = useAppSelector(
        (state) => state.pos.currentOrderVoided.items.pending.byId,
    );
    const pendingVoidedItemIds = Object.keys(pendingVoidedItemsById);
    const pendingVoidedCombosById = useAppSelector(
        (state) => state.pos.currentOrderVoided.combos.pending.byId,
    );
    const pendingVoidedComboIds = Object.keys(pendingVoidedCombosById);

    const subtotalAndTaxCents = useAppSelector((state) =>
        posSelectors.getCurrentOrderTotalCents(state, []),
    );

    const discountCents = useAppSelector((state) =>
        posSelectors.getCurrentOrderDiscountAmountCents(state),
    );

    const [splitProduct, setSplitProduct] = useState<{
        entity: Lib.selectors.CartEntityItemOrCombo;
        totalQuantity: number;
    } | null>(null);

    const orderTotalQuantity = useAppSelector((state) =>
        posSelectors.getCurrentOrderTotalQuantity(state),
    );
    const prevOrderTotalQuantity = usePrevious(orderTotalQuantity);
    const prevOrderLength = usePrevious(props.orderData.length);
    const bottomOfListRef = useRef<HTMLDivElement>(null);
    useEffect(() => {
        // NOTE: only scroll when add a new item to the order
        // (not when split, which results in the order length changing but the order total quantity remaining the same)
        if (
            prevOrderLength !== undefined &&
            prevOrderTotalQuantity !== undefined &&
            prevOrderLength < props.orderData.length &&
            prevOrderTotalQuantity < orderTotalQuantity
        ) {
            bottomOfListRef.current?.scrollIntoView({behavior: "smooth"});
        }
    }, [props.orderData.length]);

    const selectOrderType = (orderType: API.models.OrderType) => {
        dispatch(
            // NOTE: this action returns modified items resulting from changes in menu selection,
            // but since to go and dine in use the same menu, we don't need the return value
            Actions.currentOrderActions.selectOrderType(orderType),
        );

        navigate(routeHelpers.preCheckout());
    };

    const onVoidCheckoutComboOrItem = (
        type: "item" | "combo",
        customizationId: string,
        quantity: number,
    ) => {
        if (type === "item") {
            dispatch(
                posActions.addPendingVoidedItem(customizationId, quantity),
            );
        } else if (type === "combo") {
            dispatch(
                posActions.addPendingVoidedCombo(customizationId, quantity),
            );
        }
    };

    const onDeselectVoidCheckoutComboOrItem = (
        type: "item" | "combo",
        customizationId: string,
    ) => {
        if (type === "item") {
            logger.info("Deselecting void item.");
            const id = pendingVoidedItemIds.find(
                (id) =>
                    pendingVoidedItemsById[id].itemCustomizationId ===
                    customizationId,
            );
            if (id) {
                dispatch(posActions.removePendingVoidedItem(id));
            } else {
                logger.warn("Unable to find pending void item.", {
                    customizationId: customizationId,
                });
            }
        } else if (type === "combo") {
            logger.info("Deselecting void combo.");
            const id = pendingVoidedComboIds.find(
                (id) =>
                    pendingVoidedCombosById[id].comboCustomizationId ===
                    customizationId,
            );
            if (id) {
                dispatch(posActions.removePendingVoidedCombo(id));
            } else {
                logger.warn("Unable to find pending void combo.", {
                    customizationId: customizationId,
                });
            }
        }
    };

    const removeGiftCardValue = (giftCardId: string) => {
        dispatch(posActions.removeExistingGiftCardRequest(giftCardId));
    };

    const removeGiftCard = (giftCardId: string) => {
        dispatch(posActions.removeNewGiftCardRequest(giftCardId));
    };

    const updateGiftCard = (
        id: string,
        balanceCents: API.models.USDCents,
        quantity: number,
    ) => {
        dispatch(
            posActions.updateNewGiftCardRequest(id, balanceCents, quantity),
        );
    };

    const [startingNewOrder, setStartingNewOrder] = useState(false);
    const [reserveOrderErrored, setReserveOrderErrored] = useState(false);
    const startNewOrder = () => {
        if (
            stationMode !== "front_of_house_1" &&
            stationMode !== "front_of_house_2"
        ) {
            return;
        }

        logger.info("Starting new order from CashierCheckoutItems.");
        setStartingNewOrder(true);
        return dispatch(posActions.startNewOrder())
            .then(() => {
                navigate(routeHelpers.menu());
            })
            .finally(() => {
                setStartingNewOrder(false);
            });
    };

    function renderCashierButton() {
        const disabled = orderEmpty || isTillAssignmentRequired;

        if (isGiftCardsOrder) {
            return (
                <OutlineButton
                    className={styles.footerButton}
                    label={strings.proceedPayment}
                    onClick={() => navigate(routeHelpers.payment())}
                />
            );
        }

        if (
            stationMode === "front_of_house_1" ||
            stationMode === "front_of_house_2"
        ) {
            return (
                <>
                    <OutlineButton
                        className={classNames(styles.footerButton, styles.togo)}
                        label={strings.toGo}
                        onClick={() =>
                            selectOrderType(Constants.orderType.TO_GO)
                        }
                        disabled={disabled}
                    />

                    <OutlineButton
                        className={styles.footerButton}
                        label={strings.dineIn}
                        onClick={() =>
                            selectOrderType(Constants.orderType.DINE_IN)
                        }
                        disabled={disabled}
                    />
                </>
            );
        }

        if (
            stationMode === "line_buster" ||
            stationMode === "drive_thru_order_taker"
        ) {
            return (
                <OutlineButton
                    className={styles.footerButton}
                    label={strings.proceed}
                    onClick={() =>
                        selectOrderType(Constants.orderType.DRIVE_THRU)
                    }
                    disabled={disabled}
                />
            );
        }

        if (stationMode === "drive_thru_order_fulfillment") {
            return (
                <OutlineButton
                    className={styles.footerButton}
                    label={strings.proceed}
                    onClick={() => navigate(routeHelpers.payment())}
                    disabled={disabled}
                />
            );
        }

        return null;
    }

    function renderDefaultFooter() {
        // we are guaranteed that the station mode is drive thru order taker or lane buster
        if (laneAssignment === "both") {
            return (
                <div className={styles.laneChoiceAndFooterButtonContainer}>
                    {laneAssignment === "both" ? (
                        <div className={styles.laneChoiceContainer}>
                            <ToggleButton
                                className={styles.laneChoiceButton}
                                label={strings.lane1}
                                disabled={isTillAssignmentRequired}
                                checked={driveThruDetails.laneNumber === 1}
                                onChange={() => {
                                    dispatch(
                                        posActions.setCurrentOrderDriveThruDetails(
                                            {
                                                ...driveThruDetails,
                                                laneNumber: 1,
                                            },
                                        ),
                                    );
                                    dispatch(
                                        posActions.updateDriveThruOrderContents(),
                                    );
                                }}
                            />
                            <ToggleButton
                                className={styles.laneChoiceButton}
                                label={strings.lane2}
                                disabled={isTillAssignmentRequired}
                                checked={driveThruDetails.laneNumber === 2}
                                onChange={() => {
                                    dispatch(
                                        posActions.setCurrentOrderDriveThruDetails(
                                            {
                                                ...driveThruDetails,
                                                laneNumber: 2,
                                            },
                                        ),
                                    );
                                    dispatch(
                                        posActions.updateDriveThruOrderContents(),
                                    );
                                }}
                            />
                        </div>
                    ) : null}
                    <div
                        className={classNames(
                            styles.footerButtonsContainer,
                            !orderEmpty &&
                                stationMode !== "drive_thru_order_fulfillment"
                                ? styles.blueBackground
                                : null,
                        )}>
                        {renderCashierButton()}
                    </div>
                </div>
            );
        }

        return (
            <div
                className={classNames(
                    styles.footerButtonsContainer,
                    !orderEmpty &&
                        stationMode !== "drive_thru_order_fulfillment"
                        ? styles.blueBackground
                        : null,
                )}>
                {renderCashierButton()}
            </div>
        );
    }

    function renderDefaultTotalContent() {
        return (
            <div className={styles.totalContainer}>
                <div
                    className={classNames(
                        styles.totalText,
                        location.pathname === routeHelpers.orderComplete()
                            ? styles.totalTextGreyed
                            : null,
                    )}>
                    <div>{strings.total}</div>
                    <div>{strings.includesTax}</div>
                </div>

                <div
                    className={classNames(
                        styles.totalAmount,
                        location.pathname === routeHelpers.orderComplete()
                            ? styles.totalTextGreyed
                            : null,
                    )}>
                    {Lib.currency.centsToDollarString(
                        subtotalAndTaxCents - discountCents < 0
                            ? 0
                            : subtotalAndTaxCents - discountCents,
                    )}
                </div>
            </div>
        );
    }

    function renderVerticallyAlignedTotal() {
        return (
            <div className={styles.verticallyAlignedTotalContainer}>
                <div
                    className={classNames(
                        styles.totalText,
                        location.pathname === routeHelpers.orderComplete()
                            ? styles.totalTextGreyed
                            : null,
                    )}>
                    {strings.total + " " + strings.includesTax}
                </div>
                <div
                    className={classNames(
                        styles.totalAmount,
                        styles.noMargin,
                        location.pathname === routeHelpers.orderComplete()
                            ? styles.totalTextGreyed
                            : null,
                    )}>
                    {Lib.currency.centsToDollarString(
                        subtotalAndTaxCents - discountCents < 0
                            ? 0
                            : subtotalAndTaxCents - discountCents,
                    )}
                </div>
            </div>
        );
    }

    const showPendingOrderButton =
        stationMode === "drive_thru_order_fulfillment" ||
        ((stationMode === "front_of_house_1" ||
            stationMode === "front_of_house_2") &&
            pendingOrderCount > 0);

    const showStartNewOrderButton =
        (stationMode === "front_of_house_1" ||
            stationMode === "front_of_house_2") &&
        (posCurrentOrder.orderNumber !== null || startingNewOrder);
    return (
        <>
            <div
                className={classNames(
                    styles.dataContainer,
                    splitProduct && styles.noScroll,
                    props.className,
                )}>
                <div className={styles.innerDataContainer}>
                    {showPendingOrderButton ? (
                        <div
                            className={classNames(
                                styles.pendingOrdersButtonContainer,
                                styles.dataBorder,
                            )}>
                            <PendingOrdersButton
                                disabled={
                                    isTillAssignmentRequired ||
                                    isRequestingCCPayment ||
                                    isSubmittingOrder
                                }
                                onClick={() =>
                                    preventViewingKioskOrders
                                        ? setShowActiveOrderModal(true)
                                        : navigate(routeHelpers.otherOrders())
                                }
                            />
                            {showStartNewOrderButton ? (
                                <OutlineButton
                                    label={strings.startNewOrder}
                                    className={styles.startNewOrderButton}
                                    onClick={startNewOrder}
                                    loading={startingNewOrder}
                                    disabled={
                                        isTillAssignmentRequired ||
                                        isRequestingCCPayment ||
                                        isSubmittingOrder
                                    }
                                />
                            ) : null}
                        </div>
                    ) : null}
                    {props.orderData.map((x, i) => {
                        if (x.type === "item" || x.type === "combo") {
                            const productProps = {
                                className: styles.dataBorder,
                                entity: x,
                                onPressSplit: (
                                    entity: Lib.selectors.CartEntityItemOrCombo,
                                    totalQuantity: number,
                                ) => setSplitProduct({entity, totalQuantity}),
                            };

                            let voidId = null;
                            if (x.type === "item") {
                                voidId = voidedItemIds.find(
                                    (id) =>
                                        voidedItemsById[id]
                                            .itemCustomizationId ===
                                        x.customizationId,
                                );
                            } else if (x.type === "combo") {
                                voidId = voidedComboIds.find(
                                    (id) =>
                                        voidedCombosById[id]
                                            .comboCustomizationId ===
                                        x.customizationId,
                                );
                            }

                            return (
                                <Routes key={x.customizationId}>
                                    <Route
                                        path="*"
                                        element={
                                            <>
                                                <CheckoutItemOrCombo
                                                    {...productProps}
                                                    editable="editable"
                                                />
                                                {voidId ? (
                                                    <CheckoutItemOrCombo
                                                        {...productProps}
                                                        voidId={voidId}
                                                        editable="editable"
                                                    />
                                                ) : null}
                                            </>
                                        }
                                    />

                                    <Route
                                        path="/checkout/*"
                                        element={
                                            <>
                                                <CheckoutItemOrCombo
                                                    {...productProps}
                                                    editable={
                                                        isRequestingCCPayment ||
                                                        isSubmittingOrder ||
                                                        placeOrderAfterPaymentsProcessedErrored ||
                                                        stationMode ===
                                                            "drive_thru_order_fulfillment"
                                                            ? "uneditable"
                                                            : "removable"
                                                    }
                                                />
                                                {voidId ? (
                                                    <CheckoutItemOrCombo
                                                        {...productProps}
                                                        voidId={voidId}
                                                        editable={
                                                            isRequestingCCPayment ||
                                                            isSubmittingOrder ||
                                                            placeOrderAfterPaymentsProcessedErrored ||
                                                            stationMode ===
                                                                "drive_thru_order_fulfillment"
                                                                ? "uneditable"
                                                                : "removable"
                                                        }
                                                    />
                                                ) : null}
                                            </>
                                        }
                                    />

                                    <Route
                                        path="/checkout/complete"
                                        element={
                                            <>
                                                <CheckoutItemOrCombo
                                                    {...productProps}
                                                    editable="uneditable"
                                                />
                                                {voidId ? (
                                                    <CheckoutItemOrCombo
                                                        {...productProps}
                                                        voidId={voidId}
                                                        editable="uneditable"
                                                    />
                                                ) : null}
                                            </>
                                        }
                                    />

                                    <Route
                                        path="/manager/void"
                                        element={
                                            <SelectableCheckoutComboOrItem
                                                className={
                                                    i !== 0
                                                        ? styles.selectableItem
                                                        : undefined
                                                }
                                                entity={x}
                                                selected={
                                                    (x.type === "item" &&
                                                        !!pendingVoidedItemIds.find(
                                                            (id) =>
                                                                pendingVoidedItemsById[
                                                                    id
                                                                ]
                                                                    .itemCustomizationId ===
                                                                x.customizationId,
                                                        )) ||
                                                    (x.type === "combo" &&
                                                        !!pendingVoidedComboIds.find(
                                                            (id) =>
                                                                pendingVoidedCombosById[
                                                                    id
                                                                ]
                                                                    .comboCustomizationId ===
                                                                x.customizationId,
                                                        ))
                                                }
                                                onSelect={
                                                    onVoidCheckoutComboOrItem
                                                }
                                                onDeSelect={
                                                    onDeselectVoidCheckoutComboOrItem
                                                }
                                                disabled={!!voidId}
                                            />
                                        }
                                    />
                                </Routes>
                            );
                        } else if (x.type === "discount") {
                            // TODO: #91 - discount row item
                            return null;
                        }

                        return null;
                    })}
                    {addFundsGiftCards.map((r) => {
                        const baseProps = {
                            className: styles.dataBorder,
                            name: strings.giftCardValue,
                            quantity: 1,
                            formattedPrice: Lib.currency.centsToDollarString(
                                r.amountToAddCents,
                            ),
                            editTo: routeHelpers.giftCardsAddFunds(r.id),
                            onRemove: () => removeGiftCardValue(r.id),
                        };
                        return (
                            <Routes key={r.id}>
                                <Route
                                    path="*"
                                    element={
                                        <CheckoutProductBase
                                            {...baseProps}
                                            editable="editable"
                                        />
                                    }
                                />

                                <Route
                                    path="/checkout/*"
                                    element={
                                        <CheckoutProductBase
                                            {...baseProps}
                                            editable={
                                                isRequestingCCPayment ||
                                                placeOrderAfterPaymentsProcessedErrored
                                                    ? "uneditable"
                                                    : "removable"
                                            }
                                        />
                                    }
                                />
                                <Route
                                    path="/checkout/swipe-gift-card"
                                    element={
                                        <CheckoutProductBase
                                            {...baseProps}
                                            editable="uneditable"
                                        />
                                    }
                                />
                                <Route
                                    path="/checkout/complete"
                                    element={
                                        <CheckoutProductBase
                                            {...baseProps}
                                            editable="uneditable"
                                        />
                                    }
                                />

                                <Route path="/manager/void" element={null} />
                            </Routes>
                        );
                    })}
                    {purchaseGiftCards.map((gc) => {
                        const baseProps = {
                            className: styles.dataBorder,
                            name: strings.giftCard,
                            quantity: gc.quantity,
                            onChangeQuantity: (quantity: number) =>
                                updateGiftCard(
                                    gc.id,
                                    gc.balanceCents,
                                    quantity,
                                ),
                            canIncreaseQuantity: gc.quantity < MAX_QUANTITY,
                            canDecreaseQuantity: gc.quantity > 1,
                            formattedPrice: Lib.currency.centsToDollarString(
                                gc.balanceCents,
                            ),
                            editTo: routeHelpers.giftCardsPurchase(gc.id),
                            onRemove: () => removeGiftCard(gc.id),
                        };
                        return (
                            <Routes key={gc.id}>
                                <Route
                                    path="*"
                                    element={
                                        <CheckoutProductBase
                                            {...baseProps}
                                            editable="editable"
                                        />
                                    }
                                />

                                <Route
                                    path="/checkout/*"
                                    element={
                                        <CheckoutProductBase
                                            {...baseProps}
                                            editable={
                                                isRequestingCCPayment ||
                                                placeOrderAfterPaymentsProcessedErrored
                                                    ? "uneditable"
                                                    : "removable"
                                            }
                                        />
                                    }
                                />

                                <Route
                                    path="/checkout/swipe-gift-card"
                                    element={
                                        <CheckoutProductBase
                                            {...baseProps}
                                            editable="uneditable"
                                        />
                                    }
                                />

                                <Route
                                    path="/checkout/complete"
                                    element={
                                        <CheckoutProductBase
                                            {...baseProps}
                                            editable="uneditable"
                                        />
                                    }
                                />

                                <Route path="/manager/void" element={null} />
                            </Routes>
                        );
                    })}
                    {/* TODO: #254 - better route handling (hide gift cards when not on gift card specific routes) */}
                    {!isGiftCardsOrder && !props.orderData.length ? (
                        <div className={styles.emptyOrder}>
                            {strings.noItems}
                        </div>
                    ) : null}
                    {splitProduct ? (
                        <SplitModal
                            overlayClassName={styles.splitOverlay}
                            productName={splitProduct.entity.name}
                            totalQuantity={splitProduct.totalQuantity}
                            onClose={() => setSplitProduct(null)}
                            onSplit={(newQuantity: number) => {
                                if (!splitProduct) {
                                    logger.warn(
                                        "Attempting to call onSplit with no splitProduct set.",
                                    );
                                    return;
                                }

                                if (splitProduct.entity.type === "item") {
                                    dispatch(
                                        posActions.splitItem(
                                            splitProduct.entity.customizationId,
                                            newQuantity,
                                        ),
                                    );
                                    setSplitProduct(null);
                                } else if (
                                    splitProduct.entity.type === "combo"
                                ) {
                                    dispatch(
                                        posActions.splitCombo(
                                            splitProduct.entity.customizationId,
                                            newQuantity,
                                        ),
                                    );
                                    setSplitProduct(null);
                                } else {
                                    logger.warn(
                                        "Split not supported for this entity",
                                        {
                                            entityType:
                                                splitProduct.entity.type,
                                        },
                                    );
                                }
                            }}
                        />
                    ) : null}
                    <div ref={bottomOfListRef} />
                </div>
            </div>
            <div className={styles.discountAndFooterContainer}>
                {discountCents ? (
                    <div className={styles.discountContainer}>
                        <span
                            className={classNames(
                                styles.discountText,
                                styles.discountLabel,
                            )}>
                            {strings.discountApplied}
                        </span>
                        <span className={classNames(styles.discountText)}>
                            {Lib.currency.centsToDollarString(-discountCents)}
                        </span>
                    </div>
                ) : null}
                <div className={styles.footerContainer}>
                    <Routes>
                        <Route path="*" element={renderDefaultFooter()} />
                        <Route path="checkout" element={<Outlet />}>
                            <Route
                                path="*"
                                element={
                                    <div
                                        className={
                                            styles.footerButtonsContainer
                                        }>
                                        <OutlineButton
                                            className={styles.footerButton}
                                            label={strings.editOrder}
                                            onClick={() =>
                                                navigate(
                                                    isGiftCardsOrder
                                                        ? routeHelpers.giftCards()
                                                        : "/",
                                                )
                                            }
                                            disabled={
                                                location.pathname ===
                                                    routeHelpers.giftCardPurchase() ||
                                                isRequestingCCPayment ||
                                                isSubmittingOrder ||
                                                placeOrderAfterPaymentsProcessedErrored
                                            }
                                        />
                                    </div>
                                }
                            />
                            <Route
                                path="complete"
                                element={
                                    <div
                                        className={
                                            styles.footerButtonsContainer
                                        }>
                                        <OutlineButton
                                            className={styles.footerButton}
                                            disabled={true}
                                            label={strings.editOrder}
                                            onClick={() => navigate("/")}
                                        />
                                    </div>
                                }
                            />
                        </Route>
                    </Routes>

                    <Routes>
                        <Route
                            path="*"
                            element={
                                laneAssignment === "both"
                                    ? renderVerticallyAlignedTotal()
                                    : renderDefaultTotalContent()
                            }
                        />
                        <Route
                            path="checkout/*"
                            element={renderDefaultTotalContent()}
                        />
                        <Route
                            path="complete"
                            element={renderDefaultTotalContent()}
                        />
                    </Routes>
                </div>
            </div>
            {reserveOrderErrored ? (
                <ErrorModal
                    errorMessage={strings.reserveOrderError}
                    onClose={() => {
                        setReserveOrderErrored(false);
                    }}
                />
            ) : null}
            {showActiveOrderModal ? (
                <TextModal
                    onClose={() => setShowActiveOrderModal(false)}
                    title={strings.whoops}
                    content={strings.activeOrder}
                    cancelLabel={strings.ok}
                />
            ) : null}
        </>
    );
}
